import Seo from "@/seo/Seo";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { LogOut, RefreshCw, Inbox, Mail, Phone, Calendar, Car, MapPin, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vv_admin_token";
const field =
  "w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm font-light placeholder:text-silver/50 focus:outline-none focus:border-gold transition-colors";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm rounded-2xl border border-white/10 bg-abyss p-8">
        <div className="flex items-center gap-2 mb-1">
          <Lock size={16} className="text-gold" />
          <span className="text-gold text-xs tracking-[0.3em] uppercase">Admin Access</span>
        </div>
        <h1 className="font-serif-display text-white text-3xl mb-6">The VIN Verifier</h1>
        <div className="space-y-4">
          <input data-testid="admin-email" type="email" className={field} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="admin-password" type="password" className={field} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button data-testid="admin-login-btn" type="submit" disabled={loading} className="btn-accent w-full rounded-full px-6 py-3 text-sm font-medium disabled:opacity-60">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </div>
      </form>
      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("appointments");
  const [appointments, setAppointments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API}/admin/appointments`, { headers }),
        axios.get(`${API}/admin/contacts`, { headers }),
      ]);
      setAppointments(a.data);
      setContacts(c.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired, please sign in again");
        onLogout();
      } else {
        toast.error("Could not load data");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => { load(); }, [load]);

  const list = tab === "appointments" ? appointments : contacts;

  return (
    <div className="min-h-screen bg-navy">
      <header className="border-b border-white/10 bg-abyss/60 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <span className="font-serif-display text-white text-xl">The VIN Verifier <span className="text-gold text-sm">· Admin</span></span>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={load} className="text-silver hover:text-gold transition-colors p-2" title="Refresh"><RefreshCw size={17} /></button>
            <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-2 text-silver hover:text-white transition-colors text-sm"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex gap-2 mb-8">
          {[
            { k: "appointments", label: "Appointments", n: appointments.length },
            { k: "contacts", label: "Messages", n: contacts.length },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`rounded-full px-5 py-2.5 text-sm transition-colors ${tab === t.k ? "bg-gold text-navy font-medium" : "border border-white/15 text-silver hover:text-white"}`}
            >
              {t.label} <span className="opacity-70">({t.n})</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-silver text-sm py-20 text-center">Loading…</div>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-24 text-silver">
            <Inbox size={40} className="text-gold/50" />
            <p className="text-sm">No {tab} yet. New submissions will appear here.</p>
          </div>
        ) : tab === "appointments" ? (
          <div className="grid gap-4" data-testid="appointments-list">
            {appointments.map((a) => (
              <div key={a.id} data-testid="appointment-item" className="rounded-xl border border-white/10 bg-abyss p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-serif-display text-white text-2xl">{a.name}</h3>
                    <span className="inline-block mt-1 text-[10px] tracking-widest uppercase text-gold border border-gold/30 rounded-full px-2 py-0.5">{a.service}</span>
                  </div>
                  <span className="text-silver text-xs">{fmtDate(a.created_at)}</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mt-5 text-sm text-silver">
                  <a href={`tel:${a.phone}`} className="flex items-center gap-2 hover:text-gold"><Phone size={14} className="text-gold/60" /> {a.phone}</a>
                  <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-gold"><Mail size={14} className="text-gold/60" /> {a.email}</a>
                  <span className="flex items-center gap-2"><Car size={14} className="text-gold/60" /> {a.vehicle}</span>
                  <span className="flex items-center gap-2"><MapPin size={14} className="text-gold/60" /> {a.city}</span>
                  <span className="flex items-center gap-2"><Calendar size={14} className="text-gold/60" /> {a.preferred_date || "—"} {a.preferred_time || ""}</span>
                </div>
                {a.message && <p className="mt-4 text-sm text-white/80 border-t border-white/10 pt-3">{a.message}</p>}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid gap-4" data-testid="contacts-list">
            {contacts.map((m) => (
              <div key={m.id} data-testid="contact-item" className="rounded-xl border border-white/10 bg-abyss p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h3 className="font-serif-display text-white text-xl">{m.name}</h3>
                  <span className="text-silver text-xs">{fmtDate(m.created_at)}</span>
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-silver">
                  <a href={`mailto:${m.email}`} className="flex items-center gap-2 hover:text-gold"><Mail size={14} className="text-gold/60" /> {m.email}</a>
                  {m.phone && <a href={`tel:${m.phone}`} className="flex items-center gap-2 hover:text-gold"><Phone size={14} className="text-gold/60" /> {m.phone}</a>}
                </div>
                <p className="mt-4 text-sm text-white/80 border-t border-white/10 pt-3 whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </main>
      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => typeof window === "undefined" ? null : localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  return <>
    <Seo title="Admin | The VIN Verifier CA" description="Private administration." path="/admin" noindex />
    {!token ? <Login onLogin={setToken} /> : <Dashboard token={token} onLogout={logout} />}
  </>;
}
